import React from 'react';
import { Key, Eye, EyeOff, Save } from 'lucide-react';
import ErrorMessage from '../ErrorMessage';
import SuccessMessage from '../SuccessMessage';

interface SecurityTabProps {
    form: any;
    onFormChange: (field: string, value: string) => void;
    onSubmit: (e: React.FormEvent) => void;
    loading: boolean;
    error: string;
    onDismissError: () => void;
    success: string;
    onDismissSuccess: () => void;
    showPasswords: any;
    onToggleShowPassword: (field: string) => void;
}

const SecurityTab: React.FC<SecurityTabProps> = ({
    form,
    onFormChange,
    onSubmit,
    loading,
    error,
    onDismissError,
    success,
    onDismissSuccess,
    showPasswords,
    onToggleShowPassword
}) => {
    return (
        <div className="space-y-8">
            <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Güvenlik Ayarları</h3>
                <p className="text-gray-600">
                    Şifrenizi değiştirin ve güvenlik ayarlarınızı yönetin
                </p>
            </div>

            <div className="max-w-md">
                <form onSubmit={onSubmit} className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Mevcut Şifre</label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Key className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type={showPasswords.current ? 'text' : 'password'}
                                value={form.currentPassword}
                                onChange={(e) => onFormChange('currentPassword', e.target.value)}
                                className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                                placeholder="Mevcut şifrenizi girin"
                                disabled={loading}
                            />
                            <button
                                type="button"
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                onClick={() => onToggleShowPassword('current')}
                            >
                                {showPasswords.current ? (
                                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                ) : (
                                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                )}
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Yeni Şifre</label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Key className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type={showPasswords.new ? 'text' : 'password'}
                                value={form.newPassword}
                                onChange={(e) => onFormChange('newPassword', e.target.value)}
                                className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                                placeholder="Yeni şifrenizi girin"
                                disabled={loading}
                            />
                            <button
                                type="button"
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                onClick={() => onToggleShowPassword('new')}
                            >
                                {showPasswords.new ? (
                                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                ) : (
                                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                )}
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Yeni Şifre (Tekrar)</label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Key className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type={showPasswords.confirm ? 'text' : 'password'}
                                value={form.confirmPassword}
                                onChange={(e) => onFormChange('confirmPassword', e.target.value)}
                                className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                                placeholder="Yeni şifrenizi tekrar girin"
                                disabled={loading}
                            />
                            <button
                                type="button"
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                onClick={() => onToggleShowPassword('confirm')}
                            >
                                {showPasswords.confirm ? (
                                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                ) : (
                                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                )}
                            </button>
                        </div>
                    </div>

                    {error && (
                        <ErrorMessage
                            message={error}
                            onDismiss={onDismissError}
                        />
                    )}

                    {success && (
                        <SuccessMessage
                            message={success}
                            onDismiss={onDismissSuccess}
                            autoHide={true}
                        />
                    )}

                    <button
                        type="submit"
                        disabled={loading || !form.currentPassword || !form.newPassword || !form.confirmPassword}
                        className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02] disabled:transform-none"
                    >
                        <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                            {loading ? (
                                <svg className="h-5 w-5 text-red-300 group-hover:text-red-200 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : (
                                <Save className="h-5 w-5 text-red-300 group-hover:text-red-200" />
                            )}
                        </span>
                        {loading ? 'Değiştiriliyor...' : 'Şifreyi Değiştir'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default SecurityTab;
